/** Tiny IndexedDB key-value store with a localStorage fallback. */
const DB_NAME = 'tbcast-player';
const STORE = 'kv';

let dbPromise: Promise<IDBDatabase | null> | null = null;

function open(): Promise<IDBDatabase | null> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve) => {
    try {
      const req = indexedDB.open(DB_NAME, 1);
      req.onupgradeneeded = () => req.result.createObjectStore(STORE);
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
      req.onblocked = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
  return dbPromise;
}

export async function get<T>(key: string): Promise<T | undefined> {
  const db = await open();
  if (!db) {
    try {
      const raw = localStorage.getItem(`tbcast:${key}`);
      return raw ? (JSON.parse(raw) as T) : undefined;
    } catch {
      return undefined;
    }
  }
  return new Promise((resolve) => {
    const tx = db.transaction(STORE, 'readonly');
    const req = tx.objectStore(STORE).get(key);
    req.onsuccess = () => resolve(req.result as T | undefined);
    req.onerror = () => resolve(undefined);
  });
}

export async function set<T>(key: string, value: T): Promise<void> {
  const db = await open();
  if (!db) {
    try {
      localStorage.setItem(`tbcast:${key}`, JSON.stringify(value));
    } catch {
      // storage full or unavailable
    }
    return;
  }
  return new Promise((resolve) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).put(value, key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => resolve();
  });
}

export async function del(key: string): Promise<void> {
  const db = await open();
  if (!db) {
    try {
      localStorage.removeItem(`tbcast:${key}`);
    } catch {
      // ignore
    }
    return;
  }
  return new Promise((resolve) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).delete(key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => resolve();
  });
}

export async function clearAllExcept(keep: string[]): Promise<void> {
  const saved = await Promise.all(keep.map(async (k) => [k, await get(k)] as const));
  const db = await open();
  if (db) {
    await new Promise<void>((resolve) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).clear();
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
    });
  }
  for (const [k, v] of saved) if (v !== undefined) await set(k, v);
}

export const KEYS = {
  token: 'deviceToken',
  manifest: 'manifest',
  manifestEtag: 'manifestEtag',
  pop: 'popBuffer',
  executed: 'executedCommands',
  pairing: 'pairing',
} as const;
